import { useCallback, useMemo } from "react"
import { Comment } from "@/schema"
import { useQueryClient } from "@tanstack/react-query"

import { useCommentCable } from "@/hooks/useCommentCable"
import { useCommentSync } from "@/hooks/useCommentSync"

import { CommentsResponse } from "./api"
import { useCreateComment, useDeleteComment, useToggleReaction } from "./mutations"
import { useFetchComments } from "./queries"

export function useComments(commentableType: string, commentableId: string) {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ["comments", commentableType, commentableId], [commentableType, commentableId])
  const { shouldProcessUpdate } = useCommentSync()

  // React Query hooks
  const commentsQuery = useFetchComments(commentableType, commentableId)
  const createCommentMutation = useCreateComment()
  const deleteCommentMutation = useDeleteComment()
  const toggleReactionMutation = useToggleReaction()

  const refreshTaskCommentCounts = useCallback(() => {
    if (commentableType !== "Task" && commentableType !== "Project") return
    void queryClient.invalidateQueries({ queryKey: ["tasks"] })
    void queryClient.invalidateQueries({ queryKey: commentableType === "Task" ? ["task", commentableId] : ["task"] })
    void queryClient.invalidateQueries({
      queryKey: ["syncbox_email_thread"],
      predicate: ({ queryKey }) => queryKey[2] === "tasks",
    })
  }, [commentableId, commentableType, queryClient])

  // ActionCable callbacks
  const handleCommentCreated = useCallback(
    (comment: Comment) => {
      // Skip if we shouldn't process this update
      if (!shouldProcessUpdate(comment.id)) return
      refreshTaskCommentCounts()

      queryClient.setQueryData<CommentsResponse>(queryKey, (oldData) => {
        if (!oldData) return { comments: [comment] }

        // Check if comment already exists (prevent duplicates from optimistic updates)
        const exists = oldData.comments.some(
          (c) => c.id === comment.id || c.replies?.some((reply) => reply.id === comment.id)
        )
        if (exists) return oldData

        // Add new comment to the appropriate position
        if (!comment.parent_id) {
          // Root comment - add to beginning
          return { comments: [comment, ...oldData.comments] }
        } else {
          // Reply - find parent and add to its replies
          return {
            comments: oldData.comments.map((c) => {
              if (c.id === comment.parent_id) {
                return {
                  ...c,
                  replies: [...(c.replies || []), comment],
                }
              }
              return c
            }),
          }
        }
      })
    },
    [queryClient, queryKey, shouldProcessUpdate, refreshTaskCommentCounts]
  )

  const handleCommentUpdated = useCallback(
    (updatedComment: Comment) => {
      queryClient.setQueryData<CommentsResponse>(queryKey, (oldData) => {
        if (!oldData) return { comments: [] }

        return {
          comments: oldData.comments.map((comment) => {
            if (comment.id === updatedComment.id) {
              return { ...comment, ...updatedComment }
            }
            if (comment.replies) {
              return {
                ...comment,
                replies: comment.replies.map((reply) =>
                  reply.id === updatedComment.id ? { ...reply, ...updatedComment } : reply
                ),
              }
            }
            return comment
          }),
        }
      })
    },
    [queryClient, queryKey]
  )

  const handleCommentDeleted = useCallback(
    (commentId: string) => {
      if (!shouldProcessUpdate(commentId)) return
      refreshTaskCommentCounts()

      queryClient.setQueryData<CommentsResponse>(queryKey, (oldData) => {
        if (!oldData) return { comments: [] }

        // Remove comment from the list
        return {
          comments: oldData.comments.reduce<Comment[]>((acc, comment) => {
            if (comment.id === commentId) {
              // Skip this comment
              return acc
            }
            // Filter out deleted replies
            if (comment.replies && comment.replies.length > 0) {
              return [
                ...acc,
                {
                  ...comment,
                  replies: comment.replies.filter((reply) => reply.id !== commentId),
                },
              ]
            }
            return [...acc, comment]
          }, []),
        }
      })
    },
    [queryClient, queryKey, shouldProcessUpdate, refreshTaskCommentCounts]
  )

  const handleReactionAdded = useCallback(
    (reaction: { id: string; emoji: string; reactable_type: string; reactable_id: string; user_id: string }) => {
      if (reaction.reactable_type !== "Comment") return

      queryClient.setQueryData<CommentsResponse>(queryKey, (oldData) => {
        if (!oldData) return { comments: [] }

        return {
          comments: oldData.comments.map((comment) => {
            if (comment.id === reaction.reactable_id) {
              // Check if reaction already exists
              const exists = comment.reactions?.some((r) => r.id === reaction.id)
              if (exists) return comment

              const newReaction = {
                id: reaction.id,
                emoji: reaction.emoji,
                user_id: reaction.user_id, // This will be replaced by actual user ID from context
              }
              return {
                ...comment,
                reactions: [...(comment.reactions || []), newReaction],
              }
            }
            // Also check replies
            if (comment.replies) {
              return {
                ...comment,
                replies: comment.replies.map((reply) => {
                  if (reply.id === reaction.reactable_id) {
                    // Check if reaction already exists
                    const exists = reply.reactions?.some((r) => r.id === reaction.id)
                    if (exists) return reply

                    const newReaction = {
                      id: reaction.id,
                      emoji: reaction.emoji,
                      user_id: reaction.user_id,
                    }
                    return {
                      ...reply,
                      reactions: [...(reply.reactions || []), newReaction],
                    }
                  }
                  return reply
                }),
              }
            }
            return comment
          }),
        }
      })
    },
    [queryClient, queryKey]
  )

  const handleReactionRemoved = useCallback(
    (reaction: { id: string; emoji: string; reactable_type: string; reactable_id: string }) => {
      if (reaction.reactable_type !== "Comment") return

      queryClient.setQueryData<CommentsResponse>(queryKey, (oldData) => {
        if (!oldData) return { comments: [] }

        return {
          comments: oldData.comments.map((comment) => {
            if (comment.id === reaction.reactable_id) {
              return {
                ...comment,
                reactions: (comment.reactions || []).filter((r) => r.id !== reaction.id),
              }
            }
            // Also check replies
            if (comment.replies) {
              return {
                ...comment,
                replies: comment.replies.map((reply) => {
                  if (reply.id === reaction.reactable_id) {
                    return {
                      ...reply,
                      reactions: (reply.reactions || []).filter((r) => r.id !== reaction.id),
                    }
                  }
                  return reply
                }),
              }
            }
            return comment
          }),
        }
      })
    },
    [queryClient, queryKey]
  )

  const cableCallbacks = useMemo(
    () => ({
      onCommentsChanged: () => {
        refreshTaskCommentCounts()
        return queryClient.invalidateQueries({ queryKey })
      },
      onCommentCreated: handleCommentCreated,
      onCommentUpdated: handleCommentUpdated,
      onCommentDeleted: handleCommentDeleted,
      onReactionAdded: handleReactionAdded,
      onReactionRemoved: handleReactionRemoved,
    }),
    [
      queryClient,
      queryKey,
      refreshTaskCommentCounts,
      handleCommentCreated,
      handleCommentUpdated,
      handleCommentDeleted,
      handleReactionAdded,
      handleReactionRemoved,
    ]
  )

  // Set up ActionCable subscription
  useCommentCable(commentableType, commentableId, cableCallbacks)

  return {
    comments: commentsQuery.data?.comments || [],
    isLoading: commentsQuery.isLoading,
    error: commentsQuery.error,
    createComment: (variables: Parameters<typeof createCommentMutation.mutateAsync>[0]) => {
      const parent = commentsQuery.data?.comments.find((comment) => comment.id === variables.parentId)
      const promise = createCommentMutation.mutateAsync({
        ...variables,
        commentableType: parent?.commentable_type ?? variables.commentableType,
        commentableId: parent?.commentable_id ?? variables.commentableId,
      })
      promise.catch(() => undefined)
      return promise
    },
    deleteComment: (variables: { id: string }) =>
      deleteCommentMutation.mutateAsync({
        ...variables,
        commentableType,
        commentableId,
      }),
    toggleReaction: toggleReactionMutation.mutate,
    isCreating: createCommentMutation.isPending,
    isDeleting: deleteCommentMutation.isPending,
    isTogglingReaction: toggleReactionMutation.isPending,
  }
}
